import { Constants, UKC_AGILITY_LEVEL_GROUPS, getDisplayNameForLevel, getLevelsForDisplayName, IUkcAgilityLevelGroup } from "@eagerdog/constants";
import { IShow, IScore } from "@eagerdog/interfaces";
import { calculateUkcAgilityJumpHeight } from "../../../../../utils/ukcJumpHeightCalculator";

interface IRunOrderClass {
  id: string;
  showElement: string;
  level: string;
  judgeName: string;
  dogs: IRunOrderDog[];
  originalDogClass: any;
  completedDogs: string[];
}

interface IRunOrderDog {
  scoreId: string;
  armband: string;
  dogName: string;
  handlerName: string;
  breedName: string;
  originalLevel: string;
  agentName: string;
  jumpHeight: number;
}

/**
 * Process UKC Agility run order data
 * Groups dogs by judge, then by predefined level groups within each judge
 * Sorts by jump height within each level group
 */
export function processUkcAgilityRunOrder(
  selectedShow: IShow, 
  selectedRing: string, 
  allScores: IScore[]
): { processedClasses: IRunOrderClass[], totalEntries: number, completedEntries: number } {
  
  const processedClasses: IRunOrderClass[] = [];
  let totalEntries = 0;
  let completedEntries = 0;
  
  // Filter scores to only include those for the selected show
  const selectedShowIdStr = selectedShow._id?.toString();
  const showScores = allScores.filter(score => {
    const scoreShowId = (score.show_id as any)?._id || score.show_id;
    const scoreShowIdStr = scoreShowId?.toString();
    return scoreShowIdStr === selectedShowIdStr;
  });
  
  // Group scores by judge first
  const judgeMap = new Map<string, IScore[]>();
  
  showScores.forEach(score => {
    const judgeName = score.judge_name || 'Unassigned';
    if (!judgeMap.has(judgeName)) {
      judgeMap.set(judgeName, []);
    }
    judgeMap.get(judgeName)!.push(score);
  });
  
  // Get the ring to check for existing running_order
  const ring = selectedShow.rings?.find(r => r.ring === selectedRing);
  
  // Process each judge
  judgeMap.forEach((judgeScores, judgeName) => {
    // Group this judge's scores by level group
    const levelGroupMap = new Map<string, IScore[]>();
    
    judgeScores.forEach(score => {
      const level = score.dog_class?.level;
      if (!level) return;
      
      const displayName = getDisplayNameForLevel(level);
      if (!levelGroupMap.has(displayName)) {
        levelGroupMap.set(displayName, []);
      }
      
      levelGroupMap.get(displayName)!.push(score);
    });
    
    // Process each level group for this judge in the predefined order
    UKC_AGILITY_LEVEL_GROUPS.forEach((levelGroup: IUkcAgilityLevelGroup) => {
      const scores = levelGroupMap.get(levelGroup.displayName);
      if (!scores || scores.length === 0) return;
      
      // Map scores to dogs
      const dogs: IRunOrderDog[] = scores.map(score => {
        // Calculate UKC Agility jump height from height_at_withers and jump_height_type
        let jumpHeight = 0;
        if (score.height_at_withers && score.jump_height_type) {
          jumpHeight = calculateUkcAgilityJumpHeight(
            score.height_at_withers,
            score.jump_height_type
          ) || 0;
        }
        
        return {
          scoreId: score._id,
          armband: score.arm_number || '',
          dogName: score.call_name || '',
          handlerName: score.owner_name || '',
          breedName: score.breed || '',
          originalLevel: score.dog_class?.level || '',
          agentName: score.agent_name || '',
          jumpHeight: jumpHeight
        };
      });
      
      // Check if there's an existing running_order for this judge/level group and apply it
      if (ring?.running_order) {
        const runOrderEntry = ring.running_order.find(ro => ro.judge_name === judgeName);
        if (runOrderEntry) {
          // Find the dog_classes for this level group
          const relevantDogClasses = runOrderEntry.dog_classes?.filter(dc => {
            if (!dc.level) return false;
            const levelDisplayName = getDisplayNameForLevel(dc.level);
            return levelDisplayName === levelGroup.displayName;
          });
          
          // Collect all dog_run_order arrays from relevant dog_classes
          const allRunOrders: string[] = [];
          relevantDogClasses?.forEach(dc => {
            if (dc.dog_run_order && dc.dog_run_order.length > 0) {
              allRunOrders.push(...dc.dog_run_order);
            }
          });
          
          if (allRunOrders.length > 0) {
            // Sort dogs according to the saved dog_run_order
            dogs.sort((a, b) => {
              const aIndex = allRunOrders.indexOf(a.scoreId);
              const bIndex = allRunOrders.indexOf(b.scoreId);
              // If not found in order, put at end sorted by jump height then armband
              if (aIndex === -1 && bIndex === -1) {
                if (a.jumpHeight !== b.jumpHeight) return a.jumpHeight - b.jumpHeight;
                return a.armband < b.armband ? -1 : a.armband > b.armband ? 1 : 0;
              }
              if (aIndex === -1) return 1;
              if (bIndex === -1) return -1;
              return aIndex - bIndex;
            });
          } else {
            // No saved order: sort by jump height, then armband as tiebreaker (matches forms-ms fallback)
            dogs.sort((a, b) => {
              if (a.jumpHeight !== b.jumpHeight) return a.jumpHeight - b.jumpHeight;
              return a.armband < b.armband ? -1 : a.armband > b.armband ? 1 : 0;
            });
          }
        } else {
          // No saved order for this judge, sort by jump height
          dogs.sort((a, b) => a.jumpHeight - b.jumpHeight);
        }
      } else {
        // No running_order at all, sort by jump height
        dogs.sort((a, b) => a.jumpHeight - b.jumpHeight);
      }
      
      totalEntries += dogs.length;
      
      processedClasses.push({
        id: `${judgeName}-${levelGroup.displayName}`,
        showElement: levelGroup.showElement,
        level: levelGroup.displayName,
        judgeName: judgeName, // Use actual judge name
        dogs: dogs,
        originalDogClass: scores[0]?.dog_class || null,
        completedDogs: []
      });
    });
  });
  
  return {
    processedClasses,
    totalEntries,
    completedEntries
  };
}
